import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ClerkProvider, useUser, useSession, useClerk, useSignIn } from '@clerk/clerk-react';
import CosmosApp from './App';
import AdminPage from './components/AdminPage';
// Public homepages — the Cosmos v2 homepage (LandingAppNew) is now the
// canonical landing at "/". The legacy v1 landing (LandingApp) is kept
// for emergency rollback at "/home-legacy" only.
import LandingApp from './landing-page/App.original';
import { pageTitle, ROUTE_TITLES } from './utils/pageTitle';
import LandingAppNew from './landing-page/App';
import CompleteProfilePage from './components/CompleteProfilePage';
// Legacy About Us — kept for emergency rollback at "/about-legacy" only.
// Canonical About Us is now AboutUsNew at "/about".
import AboutUsPage from './components/AboutUsPage';
import AboutUsNew from './landing-page/components/AboutUsNew';
import RDSymposium from './landing-page/components/RDSymposium';
import CometPage from './landing-page/components/CometPage';
import UniversitiesPage from './landing-page/components/UniversitiesPage';
import IpIntelReportPage from './landing-page/components/IpIntelReportPage';
import IpIntelIntakePage from './landing-page/components/IpIntelIntakePage';
import IpIntelIrccPage from './landing-page/components/IpIntelIrccPage';
import { PrivacyPolicyModal } from './landing-page/components/PrivacyPolicyModal';
import { TermsOfServiceModal } from './landing-page/components/TermsOfServiceModal';
import { PrivacyPolicyPage } from './landing-page/components/PrivacyPolicyPage';
import { TermsOfServicePage } from './landing-page/components/TermsOfServicePage';
import { ForgotPasswordModal } from './components/modals/ForgotPasswordModal';
// The catch-all: asks the server whether an unrouted path is a public
// technology / university page (or an old URL of one) and falls back to
// NotFoundPage itself.
import PageResolver from './landing-page/components/techpages/PageResolver';
import { ProfileData } from './types';
import { apiService, ACCOUNT_DISABLED, ACCOUNT_DISABLED_EVENT, SESSION_EXPIRED_EVENT, SESSION_EXPIRED_FLAG } from './services/apiService';
import { AUTH_ERROR_MESSAGES } from './utils/authErrors';
import { useLogout } from './hooks/useLogout';
import ErrorBoundary from './ErrorBoundary';
import { initAnalytics, setTrackingUser } from './utils/analytics';
import { bootViewAs } from './utils/viewAs';
import ViewAsBanner, { ViewAsFailed } from './components/ViewAsBanner';
import { startVisitorTracking } from './utils/visitorTracking';
import { installCopyGuard } from './utils/copyGuard';
import {
  clearReturnAfterAuth, markSsoForward, pendingReturn, resolveAuthReturn, ReturnEntry, setResolvedReturn,
  updateReturnAfterAuth, withNext,
} from './utils/returnAfterAuth';
import { stripNextFromAddress } from './utils/returnAfterAuthNav';
import { goBackAfterAuth } from './utils/returnAfterAuthNav';

initAnalytics();
// Tech-card text cannot be copied (owner, 2026-10-12); /admin and form
// fields are exempt, and the admin can switch it off (Tech URL pages).
installCopyGuard();
// Our own record, alongside PostHog/GA/Clarity rather than instead of them —
// it carries their ids so the four can be reconciled when they disagree.
startVisitorTracking();

// Clerk publishable key - should be set via environment variable
// Fallback to the key provided (for development) import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ||
const CLERK_PUBLISHABLE_KEY = 'pk_live_Y2xlcmsudW5pdmVyc2l0eXRlY2guaW8k';

/** Set before redirect from /sso-callback → / so home shows a loader until backend login finishes */
const OAUTH_APP_SYNC_KEY = 'oauth_app_sync_pending';

// Warn if key is missing
if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('⚠️ Clerk publishable key is missing. OAuth (LinkedIn/Google) will not work.');
}

/** Full-screen message after OAuth redirect while we create your session (Clerk → your API → profile). */
const OAuthAccountSetupLoader: React.FC = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#FCFBF9] px-6 font-space-grotesk">
    <div className="w-full max-w-md text-center">
      <div className="animate-spin h-12 w-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-6" />
      <h1 className="text-xl font-semibold text-grayscale-1300 mb-2">Finishing sign-in…</h1>
      <p className="text-sm text-grayscale-1000 leading-relaxed mb-4">
        We&apos;re securely linking your Google or LinkedIn account and setting up your workspace.
        This usually takes a few seconds; on slower mobile networks it may take a bit longer.
      </p>
      <ul className="text-left text-xs text-grayscale-900 space-y-2 mx-auto max-w-xs">
        <li className="flex gap-2"><span className="text-primary-600">1.</span> Verifying your session</li>
        <li className="flex gap-2"><span className="text-primary-600">2.</span> Creating or loading your account</li>
        <li className="flex gap-2"><span className="text-primary-600">3.</span> Loading your profile</li>
      </ul>
    </div>
  </div>
);

// Component to handle Clerk authentication callback
const ClerkAuthHandler: React.FC<{ 
  onLogin: (email: string) => void;
  onAccountNotFound?: (email: string) => void;
  onOAuthAppSyncComplete?: () => void;
}> = ({ onLogin, onAccountNotFound, onOAuthAppSyncComplete }) => {
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const hasProcessedRef = useRef(false); // Use ref to prevent re-processing

  useEffect(() => {
    // Only process if both user and session are loaded, user exists, and we haven't processed yet
    if (userLoaded && sessionLoaded && user && session && !hasProcessedRef.current) {
      const processClerkAuth = async () => {
        // Check if we're in signup mode - if so, let SignUpModal handle it
        const isSignupMode = sessionStorage.getItem('clerk_signup_mode') === 'true';
        if (isSignupMode) {
          console.log('Signup mode detected - skipping auto-login, letting SignUpModal handle user data');
          hasProcessedRef.current = true; // Mark as processed to prevent auto-login
          onOAuthAppSyncComplete?.(); // Clear stale “finishing sign-in” flag if any
          return; // Let SignUpModal handle the user data extraction
        }

        // Check if this is a login mode (coming from LoginModal with LinkedIn)
        const isLoginMode = sessionStorage.getItem('clerk_login_mode') === 'true';
        
        // Check if we already have our session tokens
        // BUT still process if we came from LinkedIn OAuth (login mode)
        // AND never skip while /sso-callback set oauth_app_sync_pending — stale local userId
        // would clear the loader and leave the user on the landing page until a slow getProfile finishes.
        const existingUserId = apiService.getStoredUserId();
        const oauthAppSyncPending =
          typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem(OAUTH_APP_SYNC_KEY) === '1';
        if (existingUserId && !isLoginMode && !oauthAppSyncPending) {
          hasProcessedRef.current = true; // Mark as processed
          console.log('Already have session, skipping Clerk auth');
          onOAuthAppSyncComplete?.();
          return; // Already logged in with our system
        }

        hasProcessedRef.current = true; // Mark as processing to prevent loops
        
        try {
          // ============================================
          // LOG OAUTH PROFILE DATA (LOGIN)
          // ============================================
          console.log('🟢 ========== OAUTH LOGIN PROFILE DATA ==========');
          console.log('📋 Full Clerk User Object:', user);
          console.log('📧 Primary Email:', user.primaryEmailAddress?.emailAddress);
          console.log('📧 All Email Addresses:', user.emailAddresses);
          console.log('👤 First Name:', user.firstName);
          console.log('👤 Last Name:', user.lastName);
          console.log('🆔 User ID:', user.id);
          console.log('📸 Image URL:', user.imageUrl);
          console.log('🔗 External Accounts:', user.externalAccounts);
          console.log('📝 Public Metadata:', user.publicMetadata);
          console.log('📊 Unsafe Metadata:', user.unsafeMetadata);
          console.log('📅 Created At:', user.createdAt);
          console.log('📅 Updated At:', user.updatedAt);
          console.log('✅ Verified:', user.emailAddresses?.find(e => e.id === user.primaryEmailAddressId)?.verification?.status);
          console.log('🔐 Session:', session);
          console.log('🔐 Session ID:', session?.id);
          console.log('🔐 Session Status:', session?.status);
          
          // Log OAuth account details
          const oauthAccounts = user.externalAccounts || [];
          console.log('🔍 Processing OAuth Accounts:', oauthAccounts.length);
          for (const account of oauthAccounts) {
            const provider = account.provider as string;
            console.log(`  - Provider: ${provider}`);
            console.log(`  - Account ID: ${account.id}`);
            console.log(`  - Username: ${account.username}`);
            console.log(`  - Public Metadata:`, account.publicMetadata);
            console.log(`  - Verification Status:`, account.verification?.status);
            
            if (provider === 'oauth_linkedin' || provider === 'oauth_linkedin_oidc' || provider.includes('linkedin')) {
              console.log(`  ✅ Found LinkedIn account!`);
              const publicMetadata = account.publicMetadata as any;
              console.log(`  📋 LinkedIn Public Metadata:`, publicMetadata);
            }
            if (provider === 'oauth_google' || provider.includes('google')) {
              console.log(`  ✅ Found Google account!`);
            }
          }
          console.log('🟢 ================================================');
          
          // Get user email from Clerk
          const userEmail = user.primaryEmailAddress?.emailAddress;
          
          // A new address made straight away (admin → Users → Sign-in) is a
          // new account: onboarding comes before any return to a tech page.
          let autoCreating = false;
          // If in login mode, first check if account exists
          if (isLoginMode && userEmail) {
            try {
              const checkResponse = await apiService.checkEmail(userEmail.toLowerCase());
              if (checkResponse.success && checkResponse.data?.moved) {
                // An admin moved this account to another address: offering to
                // create one here would only be refused.
                window.alert(checkResponse.data.message || 'This e-mail address is no longer used to sign in.');
                sessionStorage.removeItem('clerk_login_mode');
                onOAuthAppSyncComplete?.();
                return;
              }
              if (checkResponse.success && !checkResponse.data?.exists && checkResponse.data?.oauth_auto_create) {
                // No account yet, and the team has new Google / LinkedIn
                // sign-ins make one: carry on, /auth/clerk/login creates it.
                autoCreating = true;
              } else if (checkResponse.success && !checkResponse.data?.exists) {
                // Account doesn't exist - notify parent to show confirmation
                console.log('Account not found for LinkedIn login, showing confirmation');
                sessionStorage.setItem('clerk_account_not_found', 'true');
                sessionStorage.setItem('clerk_pending_email', userEmail.toLowerCase());
                sessionStorage.removeItem('clerk_login_mode');
                if (onAccountNotFound) {
                  onAccountNotFound(userEmail.toLowerCase());
                }
                onOAuthAppSyncComplete?.();
                return;
              }
            } catch (error) {
              console.warn('Email check failed, proceeding with login:', error);
            }
          }
          
          // Get Clerk session token
          const clerkToken = await session.getToken();
          if (!clerkToken) {
            console.error('Failed to get Clerk session token');
            hasProcessedRef.current = false; // Reset on error to allow retry
            onOAuthAppSyncComplete?.();
            return;
          }

          console.log('Got Clerk token, authenticating with backend...');
          console.log('🔑 Clerk Token (first 50 chars):', clerkToken.substring(0, 50) + '...');

          // Clear login mode flag
          sessionStorage.removeItem('clerk_login_mode');

          // Call our backend to authenticate
          const response = await apiService.loginWithClerk(clerkToken);
          
          if (response.success && response.data) {
            console.log('Backend authentication successful, storing tokens...');
            console.log('📦 Backend Response Data:', response.data);
            // Store our session tokens
            apiService.setStoredAccessToken(response.data.access_token);
            apiService.setStoredRefreshToken(response.data.refresh_token);
            apiService.setStoredUserId(response.data.user_id);
            if (autoCreating && response.data.first_login) updateReturnAfterAuth({ newAccount: true });
            
            console.log('Tokens stored, triggering login with email:', response.data.email);
            // Trigger login with email (handleLogin clears oauth loader + session flag)
            onLogin(response.data.email);
          } else if (response.error === ACCOUNT_DISABLED) {
            // Suspended by the team. Say so and end the Clerk session (the
            // notice in AppRouter does both) rather than re-arming: a live
            // Clerk session would retry, and be refused, silently on every load.
            console.warn('Clerk sign-in refused: account suspended');
            window.dispatchEvent(new Event(ACCOUNT_DISABLED_EVENT));
            onOAuthAppSyncComplete?.();
          } else {
            console.error('Clerk authentication failed:', response.error);
            hasProcessedRef.current = false; // Reset on error to allow retry
            onOAuthAppSyncComplete?.();
          }
        } catch (error) {
          console.error('Error processing Clerk authentication:', error);
          hasProcessedRef.current = false; // Reset on error to allow retry
          onOAuthAppSyncComplete?.();
        }
      };

      processClerkAuth();
    }
  }, [user, session, userLoaded, sessionLoaded]); // Removed onLogin and isProcessing from dependencies

  // Reset hasProcessedRef when user logs out
  useEffect(() => {
    if (userLoaded && !user) {
      hasProcessedRef.current = false;
    }
  }, [user, userLoaded]);

  return null; // This component doesn't render anything
};



/** "/" — or "/?next=<page>" when this OAuth round trip started on a public
 *  page's modal (utils/returnAfterAuth). Marks the one-shot SSO forward that
 *  lets "/" honour a bare ?next= (storage lost) on this sign-in only. */
const ssoForwardTarget = (): string => {
  const path = pendingReturn(window.location.search)?.path;
  if (path) markSsoForward();
  return withNext('/', path);
};

// Component to handle OAuth callback from Clerk
const SSOCallbackHandler: React.FC = () => {
  const clerk = useClerk();
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const retryCountRef = React.useRef(0);
  const hasProcessedRef = React.useRef(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const maxRetries = 60; // Try for up to 30 seconds (60 * 500ms) - increased for production
  const retryInterval = 500; // Check every 500ms

  React.useEffect(() => {
    // Reset refs on mount
    retryCountRef.current = 0;
    hasProcessedRef.current = false;
    
    const handleCallback = async () => {
      // Prevent multiple calls
      if (hasProcessedRef.current) {
        return;
      }
      
      try {
        console.log('🔐 Processing OAuth callback...');
        console.log('Current URL:', window.location.href);
        console.log('Clerk loaded:', clerk.loaded);
        console.log('User loaded:', userLoaded);
        console.log('Session loaded:', sessionLoaded);
        console.log('URL search params:', window.location.search);
        console.log('URL hash:', window.location.hash);
        
        // Check if Clerk publishable key is set
        const publishableKey = CLERK_PUBLISHABLE_KEY;
        if (!publishableKey) {
          throw new Error('Clerk publishable key is missing. Please check your environment variables.');
        }
        console.log('✅ Clerk publishable key found:', publishableKey.substring(0, 20) + '...');
        
        // Check if Clerk JS is loaded in window (for debugging)
        const windowClerk = (window as any).Clerk;
        console.log('Window.Clerk exists:', !!windowClerk);
        
        // Check if this is actually an OAuth callback (has callback params)
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasCallbackParams = urlParams.has('__clerk_redirect_url') || 
                                  hashParams.has('__clerk_redirect_url') ||
                                  window.location.search.includes('__clerk') ||
                                  window.location.hash.includes('__clerk');
        
        if (!hasCallbackParams && clerk.loaded) {
          console.log('⚠️ No OAuth callback parameters found, but Clerk is loaded. Checking session...');
          // If no callback params but Clerk is loaded, maybe session already exists
          if (session) {
            console.log('✅ Session already exists, redirecting...');
            hasProcessedRef.current = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('clerk_signup_mode') !== 'true') {
              sessionStorage.setItem(OAUTH_APP_SYNC_KEY, '1');
            }
            navigate(ssoForwardTarget(), { replace: true });
            return;
          }
        }
        
        // Wait for Clerk to be fully loaded - check both clerk.loaded and session/user loaded
        const isClerkReady = clerk.loaded && (sessionLoaded || userLoaded);
        
        if (!isClerkReady) {
          retryCountRef.current += 1;
          console.log(`⏳ Waiting for Clerk to load... (attempt ${retryCountRef.current}/${maxRetries})`);
          console.log(`  - clerk.loaded: ${clerk.loaded}`);
          console.log(`  - sessionLoaded: ${sessionLoaded}`);
          console.log(`  - userLoaded: ${userLoaded}`);
          
          if (retryCountRef.current >= maxRetries) {
            // If Clerk still hasn't loaded after max retries
            console.error('❌ Clerk failed to load after', maxRetries, 'attempts');
            console.error('This might be due to:');
            console.error('1. Network issue loading Clerk JS from CDN');
            console.error('2. Content Security Policy (CSP) blocking Clerk');
            console.error('3. Invalid Clerk publishable key');
            console.error('4. ClerkProvider initialization issue');
            console.error('5. Slow network connection');
            
            // Try to reload the page once as a last resort
            if (!hasProcessedRef.current) {
              console.log('🔄 Attempting page reload to reinitialize Clerk...');
              hasProcessedRef.current = true;
              if (intervalRef.current) clearInterval(intervalRef.current);
              setTimeout(() => {
                window.location.reload();
              }, 2000);
              return;
            }
            
            // If we have callback params, try to process anyway
            if (hasCallbackParams) {
              console.log('⚠️ Clerk not fully loaded but callback params exist, attempting to process...');
              hasProcessedRef.current = true; // Mark as processing to attempt callback
            } else {
              throw new Error('Clerk failed to load. The page will reload automatically.');
            }
          } else {
            // Continue retrying
            return;
          }
        }
        
        // Mark as processing to prevent duplicate calls
        hasProcessedRef.current = true;
        if (intervalRef.current) clearInterval(intervalRef.current);
        console.log('✅ Clerk is ready, processing callback...');
        
        // Complete the OAuth redirect flow using Clerk's handleRedirectCallback
        // This method handles the OAuth callback and sets up the session
        // Add timeout wrapper for handleRedirectCallback
        const callbackPromise = clerk.handleRedirectCallback({
          redirectUrl: window.location.href,
        });
        
        // Add timeout for handleRedirectCallback (30 seconds)
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('handleRedirectCallback timeout after 30 seconds')), 30000);
        });
        
        try {
          await Promise.race([callbackPromise, timeoutPromise]);
        } catch (callbackError: any) {
          // If handleRedirectCallback fails or times out, check if session already exists
          console.warn('⚠️ handleRedirectCallback failed or timed out, checking if session exists:', callbackError);
          if (session) {
            console.log('✅ Session exists despite callback error, proceeding...');
          } else {
            // If it's a timeout, provide a more helpful error
            if (callbackError.message?.includes('timeout')) {
              throw new Error('OAuth callback timed out. This might be due to a slow network connection. Please try again.');
            }
            throw callbackError;
          }
        }
        
        console.log('✅ OAuth callback completed successfully');
        
        // Brief tick so Clerk can attach session (was 500ms + 500ms — noticeably slow on mobile)
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          const token = await clerk.session?.getToken();
          console.log('✅ Session token obtained:', token ? 'Token exists' : 'No token');
        } catch (tokenError) {
          console.warn('⚠️ Could not get session token immediately:', tokenError);
        }
        
        // Login (not signup) flows: home page shows “Finishing sign-in” until backend login completes
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('clerk_signup_mode') !== 'true') {
          sessionStorage.setItem(OAUTH_APP_SYNC_KEY, '1');
        }
        console.log('🔄 Redirecting to home...');
        // ?next=<page> rides along when the sign-in started on a public tech
        // page (utils/returnAfterAuth): "/" sends them back there afterwards.
        navigate(ssoForwardTarget(), { replace: true });
      } catch (err: any) {
        hasProcessedRef.current = true; // Prevent retries on error
        if (intervalRef.current) clearInterval(intervalRef.current);
        console.error('❌ OAuth callback error:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          errors: err.errors
        });
        setError(err.message || 'Failed to complete OAuth flow');
        setIsProcessing(false);
        // The sign-in failed: a later, unrelated one must not jump to the page.
        clearReturnAfterAuth();
        
        // Redirect to home after error (user can try again)
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    // Retry mechanism - check every 500ms until Clerk loads or max retries
    intervalRef.current = setInterval(() => {
      if (!hasProcessedRef.current && retryCountRef.current < maxRetries) {
        handleCallback();
      } else if (retryCountRef.current >= maxRetries && !hasProcessedRef.current) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setError('Clerk failed to load. Please refresh the page.');
        setIsProcessing(false);
      }
    }, retryInterval);

    // Also try immediately
    handleCallback();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clerk, userLoaded, sessionLoaded, navigate, session]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FCFBF9]">
        <div className="text-center">
          <p className="text-lg text-red-600 mb-2">OAuth Error</p>
          <p className="text-sm text-gray-600">{error}</p>
          <p className="text-xs text-gray-500 mt-4">Redirecting to home...</p>
        </div>
      </div>
    );
  }

  return <OAuthAccountSetupLoader />;
};

// Wrapper component for About Us page with modal support
const AboutUsPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
  const [showTermsModal, setShowTermsModal] = React.useState(false);

  return (
    <>
      <AboutUsPage
        onBack={() => navigate('/')}
        onShowPrivacyPolicy={() => setShowPrivacyModal(true)}
        onShowTermsOfService={() => setShowTermsModal(true)}
      />
      {showPrivacyModal && (
        <PrivacyPolicyModal
          isOpen={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
        />
      )}
      {showTermsModal && (
        <TermsOfServiceModal
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </>
  );
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

/** "UniversityTech.io | <Page>" for the public routes (utils/pageTitle).
 *  "/" is left to the page itself — the signed-out home says "Home", the
 *  signed-in app names the view. Any other path goes to the catch-all
 *  PageResolver, which may be a technology or university page (whose URL
 *  pattern is the server's and editable), so it titles itself once the
 *  server has answered — "Page Not Found" only when the server says so, never
 *  as a flash before a real page loads. */
const RouteTitle: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (path === '/') return;
    // The intake page names itself (the token makes the path unique).
    if (path.startsWith('/ip-intel-report/')) return;
    const known = ROUTE_TITLES[path] ?? ROUTE_TITLES[path.toLowerCase()];
    if (known) document.title = pageTitle(known);
  }, [pathname]);
  return null;
};

/** Shown on any page when the account turns out to be suspended — a call
 *  refused mid-session (apiService fires the event after clearing the stored
 *  session) or a refused Google / LinkedIn sign-in (ClerkAuthHandler). Signs
 *  out properly, Clerk included: a live Clerk session would otherwise sign
 *  them straight back in, and be refused again, on every page load. */
export const ACCOUNT_SUSPENDED_FLAG = 'utech_account_suspended';

const AccountSuspendedNotice: React.FC = () => {
  const { logout } = useLogout();
  // Kept in sessionStorage: Clerk's signOut reloads the page, which would
  // otherwise take the notice with it before anyone reads it.
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(ACCOUNT_SUSPENDED_FLAG) === '1'; } catch { return false; }
  });
  useEffect(() => {
    const onDisabled = () => {
      try { sessionStorage.setItem(ACCOUNT_SUSPENDED_FLAG, '1'); } catch { /* private mode */ }
      setOpen(true);
      logout().catch(() => { /* the stored session is already gone */ });
    };
    window.addEventListener(ACCOUNT_DISABLED_EVENT, onDisabled);
    return () => window.removeEventListener(ACCOUNT_DISABLED_EVENT, onDisabled);
  }, [logout]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[90] font-space-grotesk p-4"
         role="alertdialog" aria-modal="true" aria-labelledby="account-suspended-title">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 text-center">
        <div className="w-14 h-14 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 id="account-suspended-title" className="text-xl font-semibold text-grayscale-1300 mb-2">
          Account suspended
        </h3>
        <p className="text-sm text-grayscale-1000 leading-relaxed mb-6">{AUTH_ERROR_MESSAGES.account_disabled}</p>
        <div className="flex gap-3">
          <a href="mailto:support@universitytech.io"
             className="flex-1 px-4 py-2.5 text-sm font-medium text-grayscale-1200 bg-grayscale-200 rounded-full hover:bg-primary-100 transition-colors">
            E-mail support
          </a>
          <button onClick={() => {
              try { sessionStorage.removeItem(ACCOUNT_SUSPENDED_FLAG); } catch { /* ignore */ }
              setOpen(false);
            }} autoFocus
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

/** The sign-in ran out (apiService fired SESSION_EXPIRED_EVENT after a
 *  signed-in call was refused and could not be renewed): sign out properly,
 *  Clerk included, instead of leaving "Session expired or invalid — please
 *  sign in again" on a page that still looks signed in (owner, 2026-10-28).
 *  In the app (/, /admin) the home page then opens the sign-in modal, told
 *  why by SESSION_EXPIRED_FLAG; a public page (a tech or university page)
 *  just reloads where it is, signed out. */
const SessionExpiredHandler: React.FC = () => {
  const { logout } = useLogout();
  useEffect(() => {
    const onExpired = () => {
      const path = window.location.pathname;
      const inApp = path === '/' || path.startsWith('/admin');
      if (inApp) {
        try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch { /* private mode */ }
      }
      // At "/" the page swaps to the signed-out home by itself (MainWithClerk
      // listens too): no reload, so the sign-in modal it opens stays open.
      // This device only: the session that ran out is this one's.
      logout({ clearAllSessions: false,
               redirectTo: path === '/' ? undefined : (inApp ? '/' : path + window.location.search) })
        .catch(() => { /* the stored session is already gone */ });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [logout]);
  return null;
};

const AppRouter = () => {
  return (
    <ErrorBoundary>
      <RouteTitle />
      <AccountSuspendedNotice />
      <SessionExpiredHandler />
      <Routes>
        <Route path="/sso-callback" element={<SSOCallbackHandler />} />
        {/* Cosmos v2 routes are now the canonical experience.
            The legacy landing (LandingApp = App.original) and the legacy
            About Us (AboutUsPage / AboutUsPageWrapper) are kept as files
            but no longer routed to. /home-new and /about-new are kept as
            aliases that redirect to / and /about so any external links
            already shared keep working. */}
        <Route path="/about" element={<AboutUsNew onLogin={() => { window.location.href = '/'; }} />} />
        <Route path="/about-new" element={<Navigate to="/about" replace />} />
        {/* R&D Symposium recap — public page, no auth required. Same
            session-handoff pattern as /about: on successful login we hard
            navigate to "/" so MainWithClerk picks up the new tokens. */}
        <Route path="/rd-symposium" element={<RDSymposium onLogin={() => { window.location.href = '/'; }} />} />
        <Route path="/r-and-d-symposium" element={<Navigate to="/rd-symposium" replace />} />
        {/* Universities directory — public, no auth required. Same
            session-handoff pattern as /about and /rd-symposium. */}
        <Route path="/universities" element={<UniversitiesPage onLogin={() => { window.location.href = '/'; }} />} />

        {/* COMET Phase 1 landing + expression-of-interest form. */}
        <Route path="/comet" element={<CometPage onLogin={() => { window.location.href = '/'; }} />} />
        <Route path="/COMET" element={<Navigate to="/comet" replace />} />
        {/* IP Intel Report — public landing (price, samples, Razorpay order)
            and the tokenised Step-2 intake reached after payment. Guest
            checkout: neither needs a session. */}
        <Route path="/ip-intel-report" element={<IpIntelReportPage />} />
        <Route path="/ip-intel-report/intake/:token" element={<IpIntelIntakePage />} />
        {/* IIT Bombay × IRCC "Register interest" microsite (unlisted, noindex);
            /ip-intel-report/ircc is a short alias. */}
        <Route path="/ip-intel-report/iit-bombay" element={<IpIntelIrccPage />} />
        <Route path="/ip-intel-report/ircc" element={<Navigate to="/ip-intel-report/iit-bombay" replace />} />
        {/* Legacy About Us, deprecated. Reachable at /about-legacy for
            emergency rollback only. Remove once v2 is fully validated. */}
        <Route path="/about-legacy" element={<AboutUsPageWrapper />} />
        {/* Admin panel — gates itself on the stored Cosmos session + the
            backend's billing_admins list; no separate login. Groups (Users,
            Analytics, Billing, …) come from the server, per-admin hideable. */}
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/" element={<MainWithClerk LandingComponent={LandingAppNew} />} />
        {/* /home-new kept as an alias → / (the new homepage is now the
            default landing). Anyone hitting the old URL gets redirected. */}
        <Route path="/home-new" element={<Navigate to="/" replace />} />
        {/* Legacy landing, deprecated. Reachable at /home-legacy for
            emergency rollback only. Remove once v2 is fully validated. */}
        <Route path="/home-legacy" element={<MainWithClerk LandingComponent={LandingApp} />} />
        {/* Catch-all: public technology and university pages (URL pattern
            set on the server, admin-editable), 301s from their old URLs, and
            the 404 page for anything the server does not know. */}
        <Route path="*" element={<PageResolver />} />
      </Routes>
    </ErrorBoundary>
  );
};

// Wrapper component that provides handleLogin to ClerkAuthHandler.
// `LandingComponent` lets us route both "/" and "/home-new" through the same
// auth/session machinery while rendering different public homepages — the
// original landing on "/" and the Cosmos v2 homepage on "/home-new".
//
// `LandingComponent` is typed as `any` because the codebase has a mismatched
// `@types/react` (bigint-in-ReactNode) issue that already trips React.FC
// assignments elsewhere in this file; using a narrower type would just add
// more of the same noisy errors. Vite build ignores them.
interface MainWithClerkProps {
  LandingComponent?: any;
}

const MainWithClerk: React.FC<MainWithClerkProps> = ({ LandingComponent = LandingAppNew }) => {
    const navigate = useNavigate();
    const clerk = useClerk();
    const { session } = useSession();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [profileStatus, setProfileStatus] = useState<'incomplete' | 'complete'>('complete');
    const [showPasswordSetupModal, setShowPasswordSetupModal] = useState(false);
    const [hasPassword, setHasPassword] = useState(true);
    
    const [allProfiles, setAllProfiles] = useState<{ [email: string]: ProfileData }>({});
    const [userProfile, setUserProfile] = useState<ProfileData | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    
    // State for OAuth (LinkedIn/Google) account not found confirmation
    const [showAccountNotFoundPopup, setShowAccountNotFoundPopup] = useState(false);
    const [pendingOAuthEmail, setPendingOAuthEmail] = useState<string | null>(null);
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);

    const [showOAuthSyncLoader, setShowOAuthSyncLoader] = useState(() => {
        if (typeof sessionStorage === 'undefined') return false;
        return sessionStorage.getItem(OAUTH_APP_SYNC_KEY) === '1';
    });

    /** True while we have stored API tokens but have not finished auto-restore (e.g. slow getProfile for new accounts after signup reload). */
    const hasStoredBackendSession = () =>
        !!(apiService.getStoredUserId() && apiService.getStoredAccessToken());

    const [isRestoringSession, setIsRestoringSession] = useState(hasStoredBackendSession);

    /** Signed in from a public tech / university page as an EXISTING account
     *  (utils/returnAfterAuth): straight back to that page, no app, no
     *  onboarding. A new account goes through the app (App.tsx brings it
     *  back after onboarding). Decided once the session is known. */
    /*  Decided once per sign-in, on the first signed-in transition: only a
     *  page armed for THIS attempt counts (or a bare ?next= right after
     *  /sso-callback); ?next= is then stripped from the address so a reload
     *  of "/" stays in the app. A new account's return is handed to App. */
    const authReturnDecision = useRef<{ done: boolean; e: ReturnEntry | null }>({ done: false, e: null });
    const existingReturn = React.useMemo(() => {
        if (!isLoggedIn) return null;
        if (!authReturnDecision.current.done) {
            const e = resolveAuthReturn(window.location.search);
            authReturnDecision.current = { done: true, e };
            setResolvedReturn(e && e.newAccount ? e : null);
            stripNextFromAddress();
        }
        const e = authReturnDecision.current.e;
        return e && !e.newAccount ? e : null;
    }, [isLoggedIn]);
    const [returnFailed, setReturnFailed] = useState(false);
    useEffect(() => {
        if (!existingReturn) return;
        if (!goBackAfterAuth(existingReturn, { newAccount: false })) setReturnFailed(true);
    }, [existingReturn]);

    const isLoggedInRef = useRef(isLoggedIn);
    useEffect(() => {
        isLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn]);

    const clearOAuthAppSyncFlag = React.useCallback(() => {
        try {
            sessionStorage.removeItem(OAUTH_APP_SYNC_KEY);
        } catch { /* ignore */ }
        setShowOAuthSyncLoader(false);
    }, []);

    // If the flag is set but Clerk never finishes (edge case), don’t trap the user forever
    useEffect(() => {
        if (!showOAuthSyncLoader) return;
        const t = window.setTimeout(() => clearOAuthAppSyncFlag(), 120000);
        return () => window.clearTimeout(t);
    }, [showOAuthSyncLoader, clearOAuthAppSyncFlag]);

    // Same for post-reload session restore (getProfile can hang on bad networks)
    useEffect(() => {
        if (!isRestoringSession) return;
        const t = window.setTimeout(() => setIsRestoringSession(false), 120000);
        return () => window.clearTimeout(t);
    }, [isRestoringSession]);

    const handleNavigateToAbout = () => {
        navigate('/about');
    };
    
    // Handler for when OAuth login finds no existing account
    const handleAccountNotFound = (email: string) => {
        setPendingOAuthEmail(email);
        setShowAccountNotFoundPopup(true);
    };
    
    // Create account after OAuth login confirmation
    const handleCreateAccountConfirm = async () => {
        if (!pendingOAuthEmail || !session) return;
        
        setIsCreatingAccount(true);
        try {
            const clerkToken = await session.getToken();
            if (clerkToken) {
                // This will create the account since it doesn't exist
                const response = await apiService.loginWithClerk(clerkToken);
                if (response.success && response.data) {
                    apiService.setStoredAccessToken(response.data.access_token);
                    apiService.setStoredRefreshToken(response.data.refresh_token);
                    apiService.setStoredUserId(response.data.user_id);
                    
                    sessionStorage.removeItem('clerk_account_not_found');
                    sessionStorage.removeItem('clerk_pending_email');
                    setShowAccountNotFoundPopup(false);
                    setPendingOAuthEmail(null);
                    // A new account: onboarding before any return to a tech page.
                    updateReturnAfterAuth({ newAccount: true });
                    
                    // Trigger login
                    handleLogin(response.data.email);
                    return;
                }
                // Refused (e.g. an address an admin retired): say why instead of closing silently.
                // A suspended address gets the suspended notice (and a Clerk sign-out).
                if (!response.success && response.error === ACCOUNT_DISABLED) {
                    window.dispatchEvent(new Event(ACCOUNT_DISABLED_EVENT));
                } else if (!response.success && response.error) window.alert(response.error);
            }
        } catch (error) {
            console.error('Failed to create account:', error);
        } finally {
            setIsCreatingAccount(false);
        }
        
        setShowAccountNotFoundPopup(false);
    };
    
    // Decline account creation
    const handleCreateAccountDecline = async () => {
        setShowAccountNotFoundPopup(false);
        setPendingOAuthEmail(null);
        
        // Clear Clerk session
        sessionStorage.removeItem('clerk_account_not_found');
        sessionStorage.removeItem('clerk_pending_email');
        // No account after all: no tech page to return to either.
        clearReturnAfterAuth();
        
        try {
            await clerk.signOut();
        } catch (error) {
            console.warn('Error signing out from Clerk:', error);
        }
    };

    useEffect(() => {
        if (isLoggedIn) {
            document.body.className = 'bg-gradient-to-b from-[rgba(249,247,243,0.5)] to-[rgba(249,247,243,0.5)] bg-white font-grotesk';
        } else {
            document.body.className = 'bg-[#FCFBF9] min-h-screen font-space-grotesk text-grayscale-1200 overflow-x-hidden';
        }
    }, [isLoggedIn]);

    // Auto-login check on mount - handles page reload after OAuth stores tokens (new accounts often have a slow first getProfile)
    useEffect(() => {
        const checkAndAutoLogin = async () => {
            const storedUserId = apiService.getStoredUserId();
            const storedToken = apiService.getStoredAccessToken();

            if (!storedUserId || !storedToken) {
                setIsRestoringSession(false);
                return;
            }

            if (isLoggedInRef.current) {
                setIsRestoringSession(false);
                return;
            }

            console.log('Found stored session, attempting auto-login...');
            try {
                const profileResponse = await apiService.getProfile(storedUserId);
                if (isLoggedInRef.current) {
                    return;
                }
                if (profileResponse.success && profileResponse.data) {
                    const email = profileResponse.data.user.email;
                    if (email) {
                        console.log('Auto-login successful with email:', email);
                        setIsLoggedIn(true);
                        setUserEmail(email.toLowerCase());
                        setTrackingUser(email.toLowerCase(), { email: email.toLowerCase() });

                        const authResponse = await apiService.getAuthStatus(storedUserId);
                        if (authResponse.success && authResponse.data) {
                            const nextScreen = authResponse.data.next_screen;
                            setProfileStatus(nextScreen === 'home' ? 'complete' : 'incomplete');
                        }

                        const profile = profileResponse.data;
                        const userHasPassword = profile.user.has_password ?? true;
                        setHasPassword(userHasPassword);

                        const profileData: ProfileData = {
                            email: profile.user.email,
                            firstName: profile.user.first_name || '',
                            lastName: profile.user.last_name || '',
                            contactNumber: profile.user.phone || '',
                            countryCode: '91',
                            country: profile.user.country || '',
                            city: profile.user.city || '',
                            companyName: profile.profile.organisation_name || '',
                            designation: profile.profile.designation || '',
                            qualification: profile.profile.highest_qualification || '',
                            linkedin: profile.profile.linkedin_profile_url || '',
                            summary: profile.profile.professional_summary || '',
                            profileImage: profile.user.profile_picture_url || undefined,
                        };
                        setUserProfile(profileData);
                        setAllProfiles(prev => ({ ...prev, [email.toLowerCase()]: profileData }));
                    }
                } else if (!isLoggedInRef.current) {
                    console.log('Profile fetch failed, clearing session');
                    apiService.clearStoredAuth();
                }
            } catch (error) {
                console.error('Auto-login failed:', error);
                if (!isLoggedInRef.current) {
                    apiService.clearStoredAuth();
                }
            } finally {
                setIsRestoringSession(false);
            }
        };

        checkAndAutoLogin();
    }, []);

    const handleLogin = async (email: string) => {
        clearOAuthAppSyncFlag();
        setIsLoggedIn(true);
        const lowerEmail = email.toLowerCase();
        setUserEmail(lowerEmail);
        setTrackingUser(lowerEmail, { email: lowerEmail });
        
        const userId = apiService.getStoredUserId();
        if (userId) {
            setIsLoadingProfile(true);
            try {
                // Parallelize: previously sequential calls doubled wait time (worse on mobile latency)
                const [authResponse, profileResponse] = await Promise.all([
                    apiService.getAuthStatus(userId),
                    apiService.getProfile(userId),
                ]);

                if (authResponse.success && authResponse.data) {
                    const nextScreen = authResponse.data.next_screen;
                    setProfileStatus(nextScreen === 'home' ? 'complete' : 'incomplete');
                } else {
                    setProfileStatus('incomplete');
                }

                if (profileResponse.success && profileResponse.data) {
                    const profile = profileResponse.data;
                    const userHasPassword = profile.user.has_password ?? true;
                    setHasPassword(userHasPassword);
                    
                    const profileData: ProfileData = {
                        email: profile.user.email,
                        firstName: profile.user.first_name || '',
                        lastName: profile.user.last_name || '',
                        contactNumber: profile.user.phone || '',
                        countryCode: '91',
                        country: profile.user.country || '',
                        city: profile.user.city || '',
                        companyName: profile.profile.organisation_name || '',
                        designation: profile.profile.designation || '',
                        qualification: profile.profile.highest_qualification || '',
                        linkedin: profile.profile.linkedin_profile_url || '',
                        summary: profile.profile.professional_summary || '',
                        profileImage: profile.user.profile_picture_url || undefined,
                    };
                    setUserProfile(profileData);
                    setAllProfiles(prev => ({ ...prev, [lowerEmail]: profileData }));
                }
            } catch (error) {
                console.error('Failed to load profile:', error);
                setProfileStatus('incomplete');
            } finally {
                setIsLoadingProfile(false);
            }
        } else {
            setProfileStatus('incomplete');
        }
    };
    
    const handleLogout = () => {
        authReturnDecision.current = { done: false, e: null };
        setResolvedReturn(null);
        clearOAuthAppSyncFlag();
        setIsRestoringSession(false);
        setIsLoggedIn(false);
        setUserEmail(null);
        setUserProfile(null);
        setProfileStatus('complete');
        apiService.clearStoredAuth();
        setTrackingUser(null);
    };

    // Suspended mid-session (apiService saw 403 account_disabled): back to the
    // signed-out home. AccountSuspendedNotice (AppRouter) says why and ends
    // the Clerk session; this only resets what this page holds.
    useEffect(() => {
        const onDisabled = () => handleLogout();
        window.addEventListener(ACCOUNT_DISABLED_EVENT, onDisabled);
        // Session ran out: the same reset (SessionExpiredHandler signs out).
        window.addEventListener(SESSION_EXPIRED_EVENT, onDisabled);
        return () => {
            window.removeEventListener(ACCOUNT_DISABLED_EVENT, onDisabled);
            window.removeEventListener(SESSION_EXPIRED_EVENT, onDisabled);
        };
        // handleLogout only calls setters and stable helpers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProfileCompletion = async (updatedProfile: ProfileData) => {
        if (userEmail) {
            setAllProfiles(prev => ({...prev, [userEmail]: updatedProfile}));
        }
        
        const userId = apiService.getStoredUserId();
        if (userId) {
            try {
                const updateData = {
                    first_name: updatedProfile.firstName,
                    last_name: updatedProfile.lastName,
                    phone: updatedProfile.contactNumber,
                    city: updatedProfile.city,
                    country: updatedProfile.country,
                    organisation_name: updatedProfile.companyName,
                    designation: updatedProfile.designation,
                    qualification: updatedProfile.qualification,
                    linkedin_profile_url: updatedProfile.linkedin,
                    professional_summary: updatedProfile.summary,
                };
                
                await apiService.updateProfile(userId, updateData);
                setUserProfile(updatedProfile);
            } catch (error) {
                console.error('Failed to update profile:', error);
            }
        }
        
        if (!hasPassword) {
            setShowPasswordSetupModal(true);
        } else {
            setProfileStatus('complete');
        }
    }
    
    const handlePasswordSetupComplete = (newPassword: string) => {
        console.log('Password setup completed');
        setHasPassword(true);
        setShowPasswordSetupModal(false);
        setProfileStatus('complete');
    }
    
    const handlePasswordSetupSkip = () => {
        console.log('Password setup skipped');
        setShowPasswordSetupModal(false);
        setProfileStatus('complete');
    }
    
    const handleProfileSkip = () => {
        setProfileStatus('complete');
    }

    return (
        <>
            <ClerkAuthHandler
                onLogin={handleLogin}
                onAccountNotFound={handleAccountNotFound}
                onOAuthAppSyncComplete={clearOAuthAppSyncFlag}
            />
            
            {/* Account Not Found Popup for OAuth Login */}
            {showAccountNotFoundPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70] font-space-grotesk">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 mx-4">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Account Found</h3>
                            <p className="text-gray-600 mb-6">
                                No account found for <strong>{pendingOAuthEmail}</strong>. Would you like to create a new account?
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleCreateAccountDecline}
                                    disabled={isCreatingAccount}
                                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    No, Cancel
                                </button>
                                <button
                                    onClick={handleCreateAccountConfirm}
                                    disabled={isCreatingAccount}
                                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isCreatingAccount ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                            <span>Creating...</span>
                                        </>
                                    ) : (
                                        'Yes, Create Account'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {((showOAuthSyncLoader || isRestoringSession) && !isLoggedIn) || (existingReturn && !returnFailed) ? (
                <OAuthAccountSetupLoader />
            ) : !isLoggedIn ? (
                <LandingComponent onLogin={handleLogin} onNavigateToAbout={handleNavigateToAbout} />
            ) : isLoadingProfile ? (
                <div className="min-h-screen flex flex-col items-center justify-center bg-[#FCFBF9] px-6 font-space-grotesk">
                    <div className="animate-spin h-10 w-10 border-4 border-primary-600 border-t-transparent rounded-full mb-4" />
                    <p className="text-lg font-medium text-grayscale-1300">Loading your profile…</p>
                    <p className="text-sm text-grayscale-1000 mt-2 text-center max-w-sm">
                        Almost there — we&apos;re fetching your preferences and workspace settings.
                    </p>
                </div>
            ) : showPasswordSetupModal && profileStatus === 'incomplete' ? (
                <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-[rgba(249,247,243,0.9)] to-[rgba(249,247,243,0.9)] bg-white">
                    <ForgotPasswordModal 
                        isOpen={showPasswordSetupModal} 
                        onClose={handlePasswordSetupSkip}
                        onSetPassword={handlePasswordSetupComplete}
                        title="Set Your Password"
                        description="Secure your account by setting a password. You can skip this step and set it later from your profile."
                    />
                </div>
            ) : profileStatus === 'incomplete' && userEmail ? (
                <CompleteProfilePage 
                    onSkip={handleProfileSkip} 
                    onComplete={handleProfileCompletion} 
                    userEmail={userEmail} 
                    initialProfileData={userProfile || allProfiles[userEmail] || {
                        email: userEmail,
                        firstName: '',
                        lastName: '',
                        contactNumber: '',
                        countryCode: '91',
                        country: '',
                        city: '',
                        companyName: '',
                        designation: '',
                        qualification: '',
                        linkedin: '',
                        summary: '',
                    }} 
                    onLogout={handleLogout} 
                />
            ) : (
                <CosmosApp loggedInEmail={userEmail} onLogout={handleLogout} />
            )}
        </>
    );
};

const AppWithClerk = () => {
  return (
    <ClerkProvider 
      publishableKey={CLERK_PUBLISHABLE_KEY}
      // ClerkProvider will handle its own timeout for loading Clerk JS
      // The SSOCallbackHandler has increased timeout (30 seconds) to handle slow networks
    >
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ClerkProvider>
  );
};

const root = ReactDOM.createRoot(rootElement);
// View as member: a tab opened from Admin → User 360 claims its view before
// the app reads who is signed in (utils/viewAs.ts); every other load goes
// straight through.
bootViewAs().then(({ error }) => {
  root.render(error ? <ViewAsFailed message={error} /> : <><AppWithClerk /><ViewAsBanner /></>);
});